import './AnnotationToolsStatsTable.scss';
import { useTranslator } from '@Localization';
import _ from 'lodash';
import { ReactElement } from 'react';
import { GTable, IGColumnProps } from '../../../../../../__legacy__/components/table/GTable';
import { IGSubColumnProps } from '../../../../../../__legacy__/components/table/IGColumnProps';
import { enumDirectionToStringOrder, stringOrderToEnumDirection } from '../../../../../../__legacy__/components/table/TableHelpers';
import { SortingDirection } from '../../../../../../__legacy__/models/sortingDirection.model';
import { IAnnotationToolsStats, MarkingToolType } from '../models/AnnotationToolsStats';
import { AnnotationToolIcon, IconType } from './AnnotationToolIcon';

interface IAddedModifiedDeleted {
  id: string;
  added: number;
  modified: number;
  deleted: number;
}

interface IMainRow extends IAddedModifiedDeleted {
  email: string;
  annotationPoints: number;
  reviewPoints: number;
  editPoints: number;
  totalPoints: number;
  velocity: number;
}

interface ISubRow extends IAddedModifiedDeleted {
  type: IconType;
  text: string;
  tooltip: string;
  color?: string;
  annotationPoints: number;
  reviewPoints: number;
  editPoints: number;
  totalPoints: number;
}

interface IProps {
  items: IAnnotationToolsStats[];
  orderBy: string;
  orderDirection: SortingDirection | undefined;
  onSortChange: (orderBy: string, orderDirection: SortingDirection | undefined) => void;
}

export const AnnotationToolsStatsTable = ({ items, orderBy, orderDirection, onSortChange }: IProps): ReactElement => {
  const { t } = useTranslator();
  const columns: IGColumnProps<IMainRow>[] = [
    {
      sortBy: 'email',
      headerName: t('projects.details.stats.annotation_tools.headers.email'),
      cellClassName: 'stats-cell-bold',
      renderer: (x) => x.email,
    },
    {
      sortBy: 'added',
      headerName: t('projects.details.stats.annotation_tools.headers.added'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.added'),
      cellClassName: 'stats-cell-bold',
      renderer: (x) => x.added,
    },
    {
      sortBy: 'modified',
      headerName: t('projects.details.stats.annotation_tools.headers.modified'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.modified'),
      cellClassName: 'stats-cell-bold',
      renderer: (x) => x.modified,
    },
    {
      sortBy: 'deleted',
      headerName: t('projects.details.stats.annotation_tools.headers.deleted'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.deleted'),
      cellClassName: 'stats-cell-bold',
      renderer: (x) => x.deleted,
    },
    {
      sortBy: 'annotationPoints',
      headerName: t('projects.details.stats.annotation_tools.headers.annotationPoints'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.annotationPoints'),
      renderer: (item: IMainRow) => <span>{item.annotationPoints.toFixed(1)}</span>,
    },
    {
      sortBy: 'reviewPoints',
      headerName: t('projects.details.stats.annotation_tools.headers.reviewPoints'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.reviewPoints'),
      renderer: (item: IMainRow) => <span>{item.reviewPoints.toFixed(1)}</span>,
    },
    {
      sortBy: 'editPoints',
      headerName: t('projects.details.stats.annotation_tools.headers.editPoints'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.editPoints'),
      renderer: (item: IMainRow) => <span>{item.editPoints.toFixed(1)}</span>,
    },
    {
      sortBy: 'totalPoints',
      headerName: t('projects.details.stats.annotation_tools.headers.totalPoints'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.totalPoints'),
      renderer: (stats: IMainRow) => <b>{stats.totalPoints.toFixed(1)}</b>,
    },
    {
      sortBy: 'velocity',
      headerName: t('projects.details.stats.annotation_tools.headers.velocity'),
      headerTooltip: t('projects.details.stats.annotation_tools.tooltips.velocity'),
      renderer: (stats: IMainRow) => <>{stats.velocity.toFixed(1)}</>,
    },
  ];

  const renderAnnotationToolIconWithName = (item: ISubRow): ReactElement => (
    <div className='annotation-tools-table-icon-cell'>
      <span>
        <AnnotationToolIcon
          iconType={item.type}
          color={item.color}
          tooltip={item.tooltip}
        />
      </span>
      <span
        className='ellipsis-tool-description'
        title={item.text}
      >
        {item.text}
      </span>
    </div>
  );

  const subColumns: IGSubColumnProps<ISubRow>[] = [
    {
      renderer: renderAnnotationToolIconWithName,
    },
    { renderer: (stats: ISubRow) => stats.added },
    { renderer: (stats: ISubRow) => stats.modified },
    { renderer: (stats: ISubRow) => stats.deleted },
    { renderer: (stats: ISubRow) => stats.annotationPoints.toFixed(1) },
    { renderer: (stats: ISubRow) => stats.reviewPoints.toFixed(1) },
    { renderer: (stats: ISubRow) => stats.editPoints.toFixed(1) },
    { renderer: (stats: ISubRow) => stats.totalPoints.toFixed(1) },
    { renderer: () => <>&mdash;</> },
    { renderer: () => '' },
  ];

  const mapMarkingToolTypeToIconType = (markingToolType: MarkingToolType): IconType => {
    switch (markingToolType) {
      case MarkingToolType.Brush:
        return IconType.MarkingToolBrush;

      case MarkingToolType.Point:
        return IconType.MarkingToolPoint;

      case MarkingToolType.Polygon:
        return IconType.MarkingToolPolygon;

      case MarkingToolType.Polyline:
        return IconType.MarkingToolPolyline;

      case MarkingToolType.Rectangle:
        return IconType.MarkingToolRectangle;

      case MarkingToolType.RotatedRectangle:
        return IconType.MarkingToolRotatedRectangle;

      case MarkingToolType.Vector:
        return IconType.MarkingToolVector;
    }

    return IconType.MarkingToolPolygon;
  };

  const mapAnnotationToolStatsToFlatSubRows = (item: IAnnotationToolsStats): ISubRow[] => {
    return _.flatten(
      item.markingToolsStats.map((at) =>
        [
          {
            id: at.id,
            type: mapMarkingToolTypeToIconType(at.markingToolType),
            color: at.color,
            text: at.name,
            added: at.added,
            modified: at.modified,
            deleted: at.deleted,
            annotationPoints: at.annotationPoints,
            reviewPoints: at.reviewPoints,
            editPoints: at.editPoints,
            totalPoints: at.totalPoints,
            tooltip: t(`projects.details.stats.annotation_tools.tooltips.${at.markingToolType}`),
          },
        ].concat(
          at.attributesStats.map((a) => ({
            id: a.id,
            type: IconType.Attribute,
            text: a.text,
            added: a.added,
            modified: a.modified,
            deleted: a.deleted,
            color: '',
            annotationPoints: a.annotationPoints,
            reviewPoints: a.reviewPoints,
            editPoints: a.editPoints,
            totalPoints: a.totalPoints,
            tooltip: t(`projects.details.stats.annotation_tools.tooltips.${a.questionType}`),
          }))
        )
      )
    ).concat(
      item.questionsStats.map((q) => ({
        id: q.id,
        type: IconType.Question,
        text: q.text,
        added: q.added,
        modified: q.modified,
        deleted: q.deleted,
        color: '',
        annotationPoints: q.annotationPoints,
        reviewPoints: q.reviewPoints,
        editPoints: q.editPoints,
        totalPoints: q.totalPoints,
        tooltip: t(`projects.details.stats.annotation_tools.tooltips.${q.questionType}`),
      }))
    );
  };

  const mappedItems = items.map((i) => ({
    id: i.id,
    email: i.email,
    added: i.added,
    modified: i.modified,
    deleted: i.deleted,
    annotationPoints: i.annotationPoints,
    reviewPoints: i.reviewPoints,
    editPoints: i.editPoints,
    totalPoints: i.totalPoints,
    velocity: i.velocity,
    subRows: mapAnnotationToolStatsToFlatSubRows(i),
  }));

  const handleSortChange = (orderBy: string, orderDirection: string): void => {
    onSortChange(orderBy, stringOrderToEnumDirection(orderDirection));
  };

  return (
    <GTable
      isLoading={false}
      columns={columns}
      items={mappedItems}
      subColumns={subColumns}
      noItemsPlaceholder={t('projects.details.stats.no_statistics_have_been_generated_yet')}
      onSortChanged={handleSortChange}
      sortingModel={[{ orderBy, orderType: enumDirectionToStringOrder(orderDirection) }]}
    />
  );
};
